import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ConsultingModeConfig {
  visibleDashboardWidgets: string[];
  hiddenModules: string[];
  hideNotifications: boolean;
  hideGlobalSearch: boolean;
}

const DEFAULT_CONFIG: ConsultingModeConfig = {
  visibleDashboardWidgets: ['quick-actions'],
  hiddenModules: [
    'vertriebspartner', 
    'elearning', 
    'abrechnungen',
    'email',
    'berichte',
    'vorlagen',
    'buchhaltung',
    'hilfe-support'
  ],
  hideNotifications: true,
  hideGlobalSearch: true,
};

interface ConsultingModeContextType {
  isConsultingMode: boolean;
  toggleConsultingMode: () => void;
  exitConsultingMode: () => void;
  selectedCustomerIds: string[];
  selectedLeadIds: string[];
  setSelectedCustomerIds: (ids: string[]) => void;
  setSelectedLeadIds: (ids: string[]) => void;
  clearSelection: () => void;
  config: ConsultingModeConfig;
  updateConfig: (newConfig: Partial<ConsultingModeConfig>) => Promise<void>;
  isConfigLoading: boolean;
}

const ConsultingModeContext = createContext<ConsultingModeContextType | undefined>(undefined);

const STORAGE_KEY = 'consulting-mode-state';

export const ConsultingModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [isConsultingMode, setIsConsultingMode] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [config, setConfig] = useState<ConsultingModeConfig>(DEFAULT_CONFIG);
  const [isConfigLoading, setIsConfigLoading] = useState(true);

  // Load persisted state from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setIsConsultingMode(parsed.isConsultingMode || false);
        setSelectedCustomerIds(parsed.selectedCustomerIds || []);
        setSelectedLeadIds(parsed.selectedLeadIds || []);
      }
    } catch (e) {
      console.error('Error loading consulting mode state:', e);
    }
  }, []);

  // Save state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        isConsultingMode,
        selectedCustomerIds,
        selectedLeadIds,
      }));
    } catch (e) {
      console.error('Error saving consulting mode state:', e);
    }
  }, [isConsultingMode, selectedCustomerIds, selectedLeadIds]);

  // Load config from Supabase
  useEffect(() => {
    const loadConfig = async () => {
      if (!user?.id) {
        setIsConfigLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('beratungsmodus_config')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error loading consulting mode config:', error);
        } else if (data) {
          setConfig({
            visibleDashboardWidgets: data.visible_dashboard_widgets || DEFAULT_CONFIG.visibleDashboardWidgets,
            hiddenModules: data.hidden_modules || DEFAULT_CONFIG.hiddenModules,
            hideNotifications: data.hide_notifications ?? DEFAULT_CONFIG.hideNotifications,
            hideGlobalSearch: data.hide_global_search ?? DEFAULT_CONFIG.hideGlobalSearch,
          });
        }
      } catch (e) {
        console.error('Error loading consulting mode config:', e);
      } finally {
        setIsConfigLoading(false);
      }
    };

    loadConfig();
  }, [user?.id]);

  const toggleConsultingMode = useCallback(() => {
    setIsConsultingMode(prev => !prev);
  }, []);

  const exitConsultingMode = useCallback(() => {
    setIsConsultingMode(false);
    setSelectedCustomerIds([]);
    setSelectedLeadIds([]);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedCustomerIds([]);
    setSelectedLeadIds([]);
  }, []);

  const updateConfig = useCallback(async (newConfig: Partial<ConsultingModeConfig>) => {
    if (!user?.id) return;

    const updatedConfig = { ...config, ...newConfig };
    setConfig(updatedConfig);

    try {
      const { error } = await supabase
        .from('beratungsmodus_config')
        .upsert({
          user_id: user.id,
          user_email: user.email,
          visible_dashboard_widgets: updatedConfig.visibleDashboardWidgets,
          hidden_modules: updatedConfig.hiddenModules,
          hide_notifications: updatedConfig.hideNotifications,
          hide_global_search: updatedConfig.hideGlobalSearch,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });

      if (error) {
        console.error('Error saving consulting mode config:', error);
      }
    } catch (e) {
      console.error('Error saving consulting mode config:', e);
    }
  }, [user?.id, user?.email, config]);

  const value = useMemo(() => ({
    isConsultingMode,
    toggleConsultingMode,
    exitConsultingMode,
    selectedCustomerIds,
    selectedLeadIds,
    setSelectedCustomerIds,
    setSelectedLeadIds,
    clearSelection,
    config,
    updateConfig,
    isConfigLoading,
  }), [
    isConsultingMode,
    toggleConsultingMode,
    exitConsultingMode,
    selectedCustomerIds,
    selectedLeadIds,
    clearSelection,
    config,
    updateConfig,
    isConfigLoading,
  ]);

  return (
    <ConsultingModeContext.Provider value={value}>
      {children}
    </ConsultingModeContext.Provider>
  );
};

export const useConsultingMode = () => {
  const context = useContext(ConsultingModeContext);
  if (!context) {
    throw new Error('useConsultingMode must be used within a ConsultingModeProvider');
  }
  return context;
};
